/**
 * AI Inline Provider
 * Context provider para o sistema de IA inline
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { isFeatureEnabled } from '@/config/features';
import type { AIInlineState, AICommand, AIMenuPosition, AITooltipPosition } from '@/lib/ai/types';

interface AIInlineContextType extends AIInlineState {
  showMenu: (position: AIMenuPosition) => void;
  hideMenu: () => void;
  showTooltip: (position: AITooltipPosition) => void;
  hideTooltip: () => void;
  setSelectedText: (text: string) => void;
  setGhostText: (text: string) => void;
  clearGhostText: () => void;
  setLoading: (loading: boolean) => void;
  setStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
  executeCommand: (command: AICommand) => void;
}

type AIInlineAction =
  | { type: 'SHOW_MENU'; payload: AIMenuPosition }
  | { type: 'HIDE_MENU' }
  | { type: 'SHOW_TOOLTIP'; payload: AITooltipPosition }
  | { type: 'HIDE_TOOLTIP' }
  | { type: 'SET_SELECTED_TEXT'; payload: string }
  | { type: 'SET_GHOST_TEXT'; payload: string }
  | { type: 'CLEAR_GHOST_TEXT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_STREAMING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'EXECUTE_COMMAND'; payload: AICommand }
  | { type: 'RESET_STATE' };

const initialState: AIInlineState = {
  isActive: false,
  isLoading: false,
  isStreaming: false,
  currentCommand: null,
  selectedText: '',
  ghostText: '',
  menuVisible: false,
  tooltipVisible: false,
  error: null,
};

function aiInlineReducer(state: AIInlineState, action: AIInlineAction): AIInlineState {
  switch (action.type) {
    case 'SHOW_MENU':
      return {
        ...state,
        menuVisible: true,
        tooltipVisible: false,
        isActive: true,
      };

    case 'HIDE_MENU':
      return {
        ...state,
        menuVisible: false,
        isActive: state.tooltipVisible || state.isStreaming,
      };

    case 'SHOW_TOOLTIP':
      return {
        ...state,
        tooltipVisible: true,
        menuVisible: false,
        selectedText: action.payload.selectedText,
        isActive: true,
      };

    case 'HIDE_TOOLTIP':
      return {
        ...state,
        tooltipVisible: false,
        isActive: state.menuVisible || state.isStreaming,
      };

    case 'SET_SELECTED_TEXT':
      return {
        ...state,
        selectedText: action.payload,
      };

    case 'SET_GHOST_TEXT':
      return {
        ...state,
        ghostText: action.payload,
        isActive: true,
      };

    case 'CLEAR_GHOST_TEXT':
      return {
        ...state,
        ghostText: '',
        isActive: state.menuVisible || state.tooltipVisible,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error,
      };

    case 'SET_STREAMING':
      return {
        ...state,
        isStreaming: action.payload,
        isActive: action.payload || state.menuVisible || state.tooltipVisible,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isStreaming: false,
      };

    case 'EXECUTE_COMMAND':
      return {
        ...state,
        currentCommand: action.payload,
        isLoading: true,
        error: null,
      };

    case 'RESET_STATE':
      return initialState;

    default:
      return state;
  }
}

const AIInlineContext = createContext<AIInlineContextType | null>(null);

interface AIInlineProviderProps {
  children: React.ReactNode;
}

export const AIInlineProvider: React.FC<AIInlineProviderProps> = ({ children }) => {
  // Feature flag check - se flag OFF, não renderiza nada
  if (!isFeatureEnabled('ai_inline_editor')) {
    return <>{children}</>;
  }

  const [state, dispatch] = useReducer(aiInlineReducer, initialState);

  const showMenu = useCallback((position: AIMenuPosition) => {
    dispatch({ type: 'SHOW_MENU', payload: position });
  }, []);

  const hideMenu = useCallback(() => {
    dispatch({ type: 'HIDE_MENU' });
  }, []);

  const showTooltip = useCallback((position: AITooltipPosition) => {
    dispatch({ type: 'SHOW_TOOLTIP', payload: position });
  }, []);

  const hideTooltip = useCallback(() => {
    dispatch({ type: 'HIDE_TOOLTIP' });
  }, []);

  const setSelectedText = useCallback((text: string) => {
    dispatch({ type: 'SET_SELECTED_TEXT', payload: text });
  }, []);

  const setGhostText = useCallback((text: string) => {
    dispatch({ type: 'SET_GHOST_TEXT', payload: text });
  }, []);

  const clearGhostText = useCallback(() => {
    dispatch({ type: 'CLEAR_GHOST_TEXT' });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setStreaming = useCallback((streaming: boolean) => {
    dispatch({ type: 'SET_STREAMING', payload: streaming });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const executeCommand = useCallback((command: AICommand) => {
    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
  }, []);

  const contextValue: AIInlineContextType = {
    ...state,
    showMenu,
    hideMenu,
    showTooltip,
    hideTooltip,
    setSelectedText,
    setGhostText,
    clearGhostText,
    setLoading,
    setStreaming,
    setError,
    executeCommand,
  };

  return (
    <AIInlineContext.Provider value={contextValue}>
      {children}
    </AIInlineContext.Provider>
  );
};

export const useAIInline = (): AIInlineContextType => {
  const context = useContext(AIInlineContext);
  if (!context) {
    throw new Error('useAIInline deve ser usado dentro de AIInlineProvider');
  }
  return context;
};